import { useState, useEffect } from 'react'
import { X, Copy, Key, Shield, Download, Smartphone, Check, LogOut, Trash2, Cable, Monitor, MonitorSmartphone, Bell } from 'lucide-react'
import { useIdentity, useRegistry } from '../hooks/useWorkspace'
import type { CanonicalKeyRingEntry, TrustedDeviceInfo } from '../engine/types'
import { createWorkspaceAuthProvider } from '@secureblob/sdk/workspaceAuth'
import {
    getCurrentPushSubscriptionId,
    listNotificationDevices,
    revokeNotificationDevice,
    unsubscribeBrowserPush,
    type NotificationDeviceRecord,
} from '../engine/pushSubscription'
import { SIGNUP_ENABLED } from '../config'
import McpConnectionsSettings from './Mcp/McpConnectionsSettings'
import './ProfileSettings.css'

const PROFILE_EMOJIS = [
    '🔑', '🦊', '🐸', '🦉', '🐺', '🦅', '🐻', '🦁',
    '🐙', '🦋', '🐳', '🐬', '🦈', '🐧', '🦜', '🦩',
    '🌮', '🍕', '🎸', '🎮', '🚀', '💎', '🔥', '⚡',
    '🌈', '🌸', '🍀', '🎯', '🎪', '🛸', '🤖', '👾',
]

interface ProfileSettingsProps {
    isOpen: boolean
    onClose: () => void
    /** Optional: open directly on a specific tab (e.g. 'export' for backup key reminder) */
    initialTab?: 'mcp' | 'profile' | 'identity' | 'notifications' | 'export'
    /** When false, hides self-signup entry points. */
    signupEnabled?: boolean
}

type ProfileSettingsTab = NonNullable<ProfileSettingsProps['initialTab']>

interface NotificationWorkspaceDevices {
    blobId: string
    label: string
    emoji: string
    apiBaseUrl: string
    devices: Array<NotificationDeviceRecord & { isCurrentBrowser: boolean }>
    loadError?: string
}

interface NotificationRegistryPushEntry {
    blobId: string
    notifications?: { push?: boolean }
}

interface CurrentBrowserNotificationRevokePlan {
    unsubscribeBrowserPush: boolean
    blobIdsToDisable: string[]
}

function shortenKid(kid: string | undefined): string {
    if (!kid) return ''
    return kid.length > 12 ? kid.slice(0, 6) + '…' + kid.slice(-4) : kid
}

function formatNotificationTimestamp(value: string): string {
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en')
}

function formatNotificationPlatform(platform: string | undefined): string | null {
    if (!platform) return null
    const labels: Record<string, string> = {
        android: 'Android',
        chromeos: 'ChromeOS',
        ios: 'iOS',
        ipados: 'iPadOS',
        linux: 'Linux',
        macos: 'macOS',
        windows: 'Windows',
        unknown: 'Unknown',
    }
    return labels[platform] ?? platform
}

function getNotificationDeviceTitle(device: NotificationDeviceRecord): string {
    if (device.deviceLabel) return device.deviceLabel
    const platform = formatNotificationPlatform(device.platform)
    if (platform) return platform
    return `Device ${device.subscriptionId.slice(0, 8)}…`
}

function getCurrentBrowserNotificationRevokePlan(
    entries: NotificationRegistryPushEntry[],
    groups: NotificationWorkspaceDevices[],
    revokedBlobId: string,
    revokedSubscriptionId: string,
): CurrentBrowserNotificationRevokePlan {
    const loadedBlobIds = new Set(groups.map((group) => group.blobId))
    const hasUnknownWorkspaceState = groups.some((group) =>
        group.blobId !== revokedBlobId && typeof group.loadError === 'string' && group.loadError.length > 0)
        || entries.some((entry) =>
            entry.blobId !== revokedBlobId
            && entry.notifications?.push === true
            && !loadedBlobIds.has(entry.blobId))
    if (hasUnknownWorkspaceState) {
        return {
            unsubscribeBrowserPush: false,
            blobIdsToDisable: [revokedBlobId],
        }
    }

    const hasRemainingCurrentBrowserBinding = groups.some((group) =>
        group.devices.some((device) =>
            device.isCurrentBrowser
            && !(group.blobId === revokedBlobId && device.subscriptionId === revokedSubscriptionId)))
    if (hasRemainingCurrentBrowserBinding) {
        return {
            unsubscribeBrowserPush: false,
            blobIdsToDisable: [revokedBlobId],
        }
    }

    return {
        unsubscribeBrowserPush: true,
        blobIdsToDisable: Array.from(new Set(
            entries
                .filter((entry) => entry.notifications?.push === true && loadedBlobIds.has(entry.blobId))
                .map((entry) => entry.blobId)
                .concat(revokedBlobId),
        )),
    }
}

export default function ProfileSettings({ isOpen, onClose, initialTab, signupEnabled = SIGNUP_ENABLED }: ProfileSettingsProps) {
    const {
        hasIdentity, identity, createIdentity, importIdentity, exportIdentity,
        logoutIdentity, deleteIdentity, unlockWithWebAuthn,
        vaultExists, vaultUnlockMethod, updateProfile,
        trustDevice, getTrustedDevices, revokeTrustedDevice,
        revokeAllTrustedDevices, isCurrentDeviceTrusted,
    } = useIdentity()
    const { entries, updateEntry } = useRegistry()
    const [name, setName] = useState(identity?.name || '')
    const [emoji, setEmoji] = useState(identity?.emoji || '🔑')
    const [saving, setSaving] = useState(false)
    const [copied, setCopied] = useState<string | null>(null)
    const [expandedKey, setExpandedKey] = useState<string | null>(null)
    const [importPayload, setImportPayload] = useState('')
    const [pending, setPending] = useState(false)
    const [error, setError] = useState('')
    const [deleteConfirm, setDeleteConfirm] = useState('')
    const [showDeleteZone, setShowDeleteZone] = useState(false)
    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const [activeTab, setActiveTab] = useState<ProfileSettingsTab>(initialTab ?? 'mcp')

    // ─── Trusted Device state (§05 §10, §148) ─────────────────────────
    const [deviceTrusted, setDeviceTrusted] = useState<boolean | null>(null)
    const [deviceList, setDeviceList] = useState<TrustedDeviceInfo[]>([])
    const [deviceLoading, setDeviceLoading] = useState(false)
    const [trustLoading, setTrustLoading] = useState(false)
    const [revokeAllLoading, setRevokeAllLoading] = useState(false)
    const [showRevokeAllConfirm, setShowRevokeAllConfirm] = useState(false)

    const [notificationLoading, setNotificationLoading] = useState(false)
    const [notificationGroups, setNotificationGroups] = useState<NotificationWorkspaceDevices[]>([])
    const [notificationRevokeKey, setNotificationRevokeKey] = useState<string | null>(null)

    // Load trusted device state when export tab is active
    useEffect(() => {
        if (!isOpen || activeTab !== 'export' || !hasIdentity) return
        let cancelled = false
        const loadDevices = async () => {
            if (!cancelled) {
                setDeviceTrusted(null)
                setDeviceList([])
            }
            try {
                const [enrolled, devices] = await Promise.all([
                    isCurrentDeviceTrusted(),
                    getTrustedDevices(),
                ])
                if (!cancelled) {
                    setDeviceTrusted(enrolled)
                    setDeviceList(devices)
                }
            } catch {
                if (!cancelled) {
                    setDeviceTrusted(null)
                    setDeviceList([])
                }
            }
        }
        loadDevices()
        return () => { cancelled = true }
    }, [isOpen, activeTab, hasIdentity, isCurrentDeviceTrusted, getTrustedDevices])

    useEffect(() => {
        if (!isOpen || activeTab !== 'notifications' || !hasIdentity) return

        let cancelled = false
        setNotificationLoading(true)
        setNotificationGroups([])

        const loadNotificationDevices = async () => {
            const currentSubscriptionId = await getCurrentPushSubscriptionId().catch(() => null)
            const groups = await Promise.all(
                [...entries]
                    .filter((entry) => entry.canWrite !== false)
                    .sort((a, b) => a.label.localeCompare(b.label, 'en'))
                    .map(async (entry): Promise<NotificationWorkspaceDevices> => {
                        const authProvider = createWorkspaceAuthProvider(entry.apiBaseUrl, entry.capability, entry.blobId)
                        if (!authProvider) {
                            return {
                                blobId: entry.blobId,
                                label: entry.label,
                                emoji: entry.emoji,
                                apiBaseUrl: entry.apiBaseUrl,
                                devices: [],
                                loadError: 'Notification device management requires write access.',
                            }
                        }

                        try {
                            const devices = await listNotificationDevices(entry.apiBaseUrl, entry.blobId, authProvider)
                            return {
                                blobId: entry.blobId,
                                label: entry.label,
                                emoji: entry.emoji,
                                apiBaseUrl: entry.apiBaseUrl,
                                devices: devices.map((device) => ({
                                    ...device,
                                    isCurrentBrowser: currentSubscriptionId !== null && device.subscriptionId === currentSubscriptionId,
                                })),
                            }
                        } catch (err) {
                            return {
                                blobId: entry.blobId,
                                label: entry.label,
                                emoji: entry.emoji,
                                apiBaseUrl: entry.apiBaseUrl,
                                devices: [],
                                loadError: err instanceof Error ? err.message : 'Failed to load notification devices.',
                            }
                        }
                    }),
            )

            if (!cancelled) {
                setNotificationGroups(groups)
                setNotificationLoading(false)
            }
        }

        void loadNotificationDevices()
        return () => { cancelled = true }
    }, [activeTab, entries, hasIdentity, isOpen])

    // Sync activeTab when initialTab prop changes (e.g. opening from GuestBanner with 'export')
    useEffect(() => {
        if (initialTab) setActiveTab(initialTab)
    }, [initialTab])

    // Sync local name/emoji state when identity changes (after unlock, import, etc.)
    useEffect(() => {
        if (identity) {
            setName(identity.name || '')
            setEmoji(identity.emoji || '🔑')
        }
    }, [identity])

    const keyRing: CanonicalKeyRingEntry[] = identity?.keyRing ?? []
    const canCreateProfile = signupEnabled

    function handleCopy(text: string, id: string) {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(id)
            setTimeout(() => setCopied(null), 2000)
        }).catch(() => { /* clipboard unavailable — silently ignore */ })
    }

    async function handleExportIdentity() {
        const payload = await exportIdentity()
        handleCopy(payload, 'export')
    }

    async function handleCreateIdentity() {
        if (!canCreateProfile) {
            setError('New profile signup is temporarily unavailable. Import a backup key to sign in.')
            return
        }

        setPending(true)
        setError('')
        try {
            const newId = await createIdentity({ webAuthnOnly: true })
            if (!newId) return // User cancelled WebAuthn — stay as guest
            setName(newId.name)
            setEmoji(newId.emoji)
            onClose() // Auto-close profile panel after successful creation
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to create identity')
        } finally {
            setPending(false)
        }
    }

    async function handleImportIdentity() {
        if (!importPayload.trim()) return
        setPending(true)
        setError('')
        try {
            const newId = await importIdentity(importPayload.trim())
            setName(newId.name)
            setEmoji(newId.emoji)
            setImportPayload('')
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to import identity')
        } finally {
            setPending(false)
        }
    }

    async function handleUnlockPasskey() {
        setPending(true)
        setError('')
        try {
            const ok = await unlockWithWebAuthn()
            if (!ok) setError('Upplåsning avbruten eller nekad.')
            else onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte låsa upp')
        } finally {
            setPending(false)
        }
    }

    async function handleSaveProfile() {
        setSaving(true)
        try {
            await updateProfile({ name, emoji })

            // Best-effort: try to update the keychain credential name (WebAuthn L3)
            try {
                const pk = window.PublicKeyCredential as any
                if (pk?.signalCurrentUserDetails) {
                    await pk.signalCurrentUserDetails({ name, displayName: name })
                }
            } catch { /* ignore — API may not exist */ }

            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte spara profil')
        } finally {
            setSaving(false)
        }
    }

    function handleLogout() {
        logoutIdentity()
        onClose()
    }

    async function handleDelete() {
        if (deleteConfirm !== 'RADERA') return
        setPending(true)
        try {
            await deleteIdentity()
            setDeleteConfirm('')
            setShowDeleteZone(false)
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete profile')
        } finally {
            setPending(false)
        }
    }

    async function handleTrustDevice() {
        setTrustLoading(true)
        setError('')
        try {
            await trustDevice()
            setDeviceTrusted(true)
            // Refresh device list
            const devices = await getTrustedDevices()
            setDeviceList(devices)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte aktivera trusted device')
        } finally {
            setTrustLoading(false)
        }
    }

    async function handleRevokeDevice(deviceId: string) {
        setDeviceLoading(true)
        setError('')
        try {
            await revokeTrustedDevice(deviceId)
            // Refresh state
            const [enrolled, devices] = await Promise.all([
                isCurrentDeviceTrusted(),
                getTrustedDevices(),
            ])
            setDeviceTrusted(enrolled)
            setDeviceList(devices)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte ta bort enhet')
        } finally {
            setDeviceLoading(false)
        }
    }

    async function handleRevokeAllDevices() {
        setRevokeAllLoading(true)
        setError('')
        try {
            await revokeAllTrustedDevices()
            setDeviceTrusted(false)
            setDeviceList([])
            setShowRevokeAllConfirm(false)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte ta bort alla enheter')
        } finally {
            setRevokeAllLoading(false)
        }
    }

    async function handleRevokeNotificationRegistration(blobId: string, subscriptionId: string, isCurrentBrowser: boolean) {
        const entry = entries.find((item) => item.blobId === blobId)
        if (!entry) {
            setError('Workspace not found in registry.')
            return
        }

        const authProvider = createWorkspaceAuthProvider(entry.apiBaseUrl, entry.capability, blobId)
        if (!authProvider) {
            setError('Notification device management requires write access.')
            return
        }

        setNotificationRevokeKey(`${blobId}:${subscriptionId}`)
        setError('')

        try {
            await revokeNotificationDevice(entry.apiBaseUrl, blobId, subscriptionId, authProvider)
            const currentBrowserCleanupPlan = isCurrentBrowser
                ? getCurrentBrowserNotificationRevokePlan(entries, notificationGroups, blobId, subscriptionId)
                : null
            setNotificationGroups((groups) => groups.map((group) => group.blobId === blobId
                ? {
                    ...group,
                    devices: group.devices.filter((device) => device.subscriptionId !== subscriptionId),
                }
                : group))

            if (isCurrentBrowser) {
                for (const targetBlobId of currentBrowserCleanupPlan?.blobIdsToDisable ?? [blobId]) {
                    const targetEntry = entries.find((item) => item.blobId === targetBlobId)
                    updateEntry(targetBlobId, {
                        notifications: {
                            ...targetEntry?.notifications,
                            push: false,
                        },
                    })
                }

                if (currentBrowserCleanupPlan?.unsubscribeBrowserPush) {
                    await unsubscribeBrowserPush().catch(() => {})
                }
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to revoke notification device.')
        } finally {
            setNotificationRevokeKey(null)
        }
    }

    if (!isOpen) return null

    // Determine auth state
    // Only show locked UI for WebAuthn vaults (the only production case).
    // PIN vaults are stale/legacy → fall through to onboarding.
    const isLocked = !hasIdentity && vaultExists === true && (vaultUnlockMethod === 'webauthn' || vaultUnlockMethod === 'webauthn-prf')

    return (
        <>
            <div className="profile-backdrop" onClick={onClose} />
            <div className="profile-modal">
                <div className="profile-header">
                    <h2>Profile Settings</h2>
                    <button className="btn btn-icon profile-close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {hasIdentity && (
                    <div className="profile-tabs">
                        <button
                            className={`profile-tab ${activeTab === 'mcp' ? 'active' : ''}`}
                            onClick={() => setActiveTab('mcp')}
                        >
                            MCP
                        </button>
                        <button
                            className={`profile-tab ${activeTab === 'profile' ? 'active' : ''}`}
                            onClick={() => setActiveTab('profile')}
                        >
                            Profile
                        </button>
                        <button
                            className={`profile-tab ${activeTab === 'identity' ? 'active' : ''}`}
                            onClick={() => setActiveTab('identity')}
                        >
                            Identity
                        </button>
                        <button
                            className={`profile-tab ${activeTab === 'notifications' ? 'active' : ''}`}
                            onClick={() => setActiveTab('notifications')}
                        >
                            Notifications
                        </button>
                        <button
                            className={`profile-tab ${activeTab === 'export' ? 'active' : ''}`}
                            onClick={() => setActiveTab('export')}
                        >
                            Account
                        </button>
                    </div>
                )}

                <div className="profile-content">
                    {error ? <p className="profile-hint" role="alert">{error}</p> : null}
                    {hasIdentity ? (
                        <>
                            {activeTab === 'profile' && (
                                <section className="profile-section">
                                    <h3><Shield size={16} /> Profile</h3>
                                    <div className="profile-avatar-row">
                                        <div className="profile-emoji-wrapper">
                                            <button className="profile-emoji-btn" title="Change emoji" onClick={() => setShowEmojiPicker(v => !v)}>
                                                {emoji}
                                            </button>
                                            {showEmojiPicker && (
                                                <div className="profile-emoji-picker">
                                                    {PROFILE_EMOJIS.map(e => (
                                                        <button
                                                            key={e}
                                                            className={`profile-emoji-option ${e === emoji ? 'active' : ''}`}
                                                            onClick={() => { setEmoji(e); setShowEmojiPicker(false) }}
                                                        >
                                                            {e}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="profile-avatar-fields">
                                            <input
                                                type="text"
                                                value={name}
                                                onChange={e => setName(e.target.value)}
                                                placeholder="Your name"
                                                className="profile-input"
                                                maxLength={64}
                                            />
                                        </div>
                                    </div>
                                    <button
                                        className="profile-save-btn"
                                        onClick={handleSaveProfile}
                                        disabled={saving}
                                    >
                                        {saving ? 'Sparar…' : 'Spara profil'}
                                    </button>
                                </section>
                            )}

                            {activeTab === 'identity' && (
                                <section className="profile-section">
                                    <h3><Key size={16} /> Identity</h3>
                                    <div className="profile-kid-row">
                                        <span className="profile-kid-label">Primary kid</span>
                                        <code className="profile-kid-value">{shortenKid(identity?.kid)}</code>
                                        <button
                                            className="btn btn-icon btn-xs"
                                            onClick={() => handleCopy(identity?.kid || '', 'kid')}
                                            title="Copy kid"
                                        >
                                            {copied === 'kid' ? <Check size={14} /> : <Copy size={14} />}
                                        </button>
                                    </div>

                                    <div className="profile-keyring">
                                        <span className="profile-keyring-label">Key ring ({keyRing.length} keys)</span>
                                        {keyRing.map(key => (
                                            <div
                                                key={key.kid}
                                                className={`profile-key ${expandedKey === key.kid ? 'expanded' : ''}`}
                                                onClick={() => setExpandedKey(expandedKey === key.kid ? null : key.kid)}
                                            >
                                                <div className="profile-key-summary">
                                                    <span className={`profile-key-badge ${key.source}`}>
                                                        {key.source === 'primary' ? '🔑' : '🔗'}
                                                    </span>
                                                    <span className="profile-key-label">{key.source === 'primary' ? 'Primary key' : 'Linked key'}</span>
                                                    <code className="profile-key-kid">{shortenKid(key.kid)}</code>
                                                </div>
                                                {expandedKey === key.kid && (
                                                    <div className="profile-key-details">
                                                        <div><strong>Kid:</strong> <code>{key.kid}</code></div>
                                                        <div><strong>Source:</strong> {key.source}</div>
                                                        {key.blobId && <div><strong>Blob:</strong> {key.blobId}</div>}
                                                        {key.pk && <div><strong>pk:</strong> <code>{shortenKid(key.pk)}</code></div>}
                                                        <div><strong>Added:</strong> {new Date(key.addedAt).toLocaleDateString('en')}</div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {activeTab === 'notifications' && (
                                <section className="profile-section">
                                    <h3><Bell size={16} /> Notifications</h3>
                                    <p className="profile-hint">
                                        Registered notification devices are grouped per workspace.
                                        Revoking removes only that workspace binding, not every workspace on the same browser.
                                    </p>

                                    {notificationLoading ? (
                                        <p className="profile-hint profile-hint-subtle">Loading registered devices…</p>
                                    ) : notificationGroups.length === 0 ? (
                                        <p className="profile-hint profile-hint-subtle">
                                            No writable workspaces are available for notification device management.
                                        </p>
                                    ) : (
                                        <div className="profile-notification-groups">
                                            {notificationGroups.map((group) => (
                                                <div key={group.blobId} className="profile-notification-workspace">
                                                    <div className="profile-notification-workspace-header">
                                                        <div className="profile-notification-workspace-title">
                                                            <span className="profile-notification-workspace-emoji">{group.emoji}</span>
                                                            <div>
                                                                <div className="profile-notification-workspace-label">{group.label}</div>
                                                                <div className="profile-notification-workspace-blobid">{group.blobId}</div>
                                                            </div>
                                                        </div>
                                                        <span className="profile-notification-count">{group.devices.length}</span>
                                                    </div>

                                                    {group.loadError ? (
                                                        <p className="profile-hint profile-hint-subtle">{group.loadError}</p>
                                                    ) : group.devices.length === 0 ? (
                                                        <p className="profile-hint profile-hint-subtle">No registered devices for this workspace.</p>
                                                    ) : (
                                                        <div className="profile-notification-device-list">
                                                            {group.devices.map((device) => {
                                                                const busyKey = `${group.blobId}:${device.subscriptionId}`
                                                                const platform = formatNotificationPlatform(device.platform)
                                                                return (
                                                                    <div key={device.subscriptionId} className="profile-notification-device">
                                                                        <div className="profile-notification-device-copy">
                                                                            <div className="profile-notification-device-label-row">
                                                                                <span className="profile-notification-device-label">
                                                                                    {getNotificationDeviceTitle(device)}
                                                                                </span>
                                                                                {device.isCurrentBrowser && (
                                                                                    <span className="profile-notification-badge current">This browser</span>
                                                                                )}
                                                                                {platform && (
                                                                                    <span className="profile-notification-badge">{platform}</span>
                                                                                )}
                                                                            </div>
                                                                            <div className="profile-notification-device-meta">
                                                                                Last active {formatNotificationTimestamp(device.lastSeenAt)}
                                                                            </div>
                                                                            <div className="profile-notification-device-meta subtle">
                                                                                Added {formatNotificationTimestamp(device.createdAt)}
                                                                            </div>
                                                                        </div>
                                                                        <button
                                                                            className="btn btn-ghost btn-xs profile-notification-revoke"
                                                                            disabled={notificationRevokeKey === busyKey}
                                                                            onClick={() => handleRevokeNotificationRegistration(group.blobId, device.subscriptionId, device.isCurrentBrowser)}
                                                                        >
                                                                            {notificationRevokeKey === busyKey ? 'Revoking…' : 'Revoke'}
                                                                        </button>
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </section>
                            )}

                            {activeTab === 'mcp' && (
                                <section className="profile-section">
                                    <h3><Cable size={16} /> MCP</h3>
                                    <McpConnectionsSettings />
                                </section>
                            )}

                            {activeTab === 'export' && (
                                <>
                                    <section className="profile-section">
                                        <h3><Smartphone size={16} /> Backup Key</h3>
                                        <p className="profile-hint">
                                            Copy your backup key to sign in from another device.
                                            <strong> Never share this key!</strong>
                                        </p>
                                        <button className="profile-export-btn" onClick={handleExportIdentity} disabled={pending}>
                                            {copied === 'export' ? (
                                                <><Check size={16} /> Copied!</>
                                            ) : (
                                                <><Download size={16} /> Copy Backup Key</>
                                            )}
                                        </button>
                                    </section>

                                    {/* ─── Trusted Devices (§05 §10, §148) ─────────────── */}
                                    <section className="profile-section">
                                        <h3><Monitor size={16} /> Trusted Devices</h3>
                                        <p className="profile-hint">
                                            Trust this browser to skip passkey prompts on reload.
                                            You can revoke individual devices or all devices at any time.
                                        </p>

                                        {deviceTrusted === null ? (
                                            <p className="profile-hint profile-hint-subtle">Loading…</p>
                                        ) : deviceTrusted ? (
                                            <div className="profile-device-current">
                                                <MonitorSmartphone size={16} />
                                                <span>This device is <strong>trusted</strong></span>
                                            </div>
                                        ) : (
                                            <button
                                                className="profile-export-btn"
                                                onClick={handleTrustDevice}
                                                disabled={trustLoading}
                                            >
                                                {trustLoading ? 'Enrolling…' : <><Shield size={16} /> Trust This Device</>}
                                            </button>
                                        )}

                                        {deviceList.length > 0 && (
                                            <div className="profile-device-list">
                                                {deviceList.map(device => (
                                                    <div key={device.deviceId} className="profile-device-item">
                                                        <div className="profile-device-info">
                                                            <span className="profile-device-label">
                                                                {device.label || `Device ${device.deviceId.slice(0, 8)}…`}
                                                            </span>
                                                            {device.revokedAt && (
                                                                <span className="profile-device-revoked-badge">Revoked</span>
                                                            )}
                                                            <span className="profile-device-date">
                                                                Last seen {new Date(device.lastSeenAt).toLocaleDateString('en')}
                                                            </span>
                                                        </div>
                                                        {!device.revokedAt && (
                                                            <button
                                                                className="btn btn-ghost btn-xs"
                                                                onClick={() => handleRevokeDevice(device.deviceId)}
                                                                disabled={deviceLoading}
                                                                title="Remove this trusted device"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {deviceList.length > 1 && !showRevokeAllConfirm && (
                                            <button
                                                className="profile-delete-toggle"
                                                onClick={() => setShowRevokeAllConfirm(true)}
                                                style={{ marginTop: '0.5rem' }}
                                            >
                                                <LogOut size={14} /> Logout all devices…
                                            </button>
                                        )}

                                        {showRevokeAllConfirm && (
                                            <div className="profile-delete-confirm" style={{ marginTop: '0.5rem' }}>
                                                <p className="profile-delete-warning">
                                                    ⚠️ All trusted devices will be revoked. You will need to use passkey/PIN on every device next time.
                                                </p>
                                                <div className="profile-delete-actions">
                                                    <button
                                                        className="profile-delete-btn"
                                                        disabled={revokeAllLoading}
                                                        onClick={handleRevokeAllDevices}
                                                    >
                                                        <LogOut size={14} /> Revoke all devices
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost"
                                                        onClick={() => setShowRevokeAllConfirm(false)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </section>

                                    <section className="profile-section">
                                        <button className="profile-logout-btn" onClick={handleLogout}>
                                            <LogOut size={16} /> Logga ut
                                        </button>
                                        <p className="profile-hint profile-hint-subtle">
                                            Din profil sparas — du kan logga in igen nästa gång.
                                        </p>
                                    </section>

                                    <section className="profile-section profile-danger-zone">
                                        {!showDeleteZone ? (
                                            <button
                                                className="profile-delete-toggle"
                                                onClick={() => setShowDeleteZone(true)}
                                            >
                                                <Trash2 size={14} /> Radera profil…
                                            </button>
                                        ) : (
                                            <div className="profile-delete-confirm">
                                                <p className="profile-delete-warning">
                                                    ⚠️ Alla dina nycklar raderas permanent. Du förlorar access till dina workspaces om du inte har en export-URL.
                                                </p>
                                                <label className="profile-delete-label">
                                                    Skriv <strong>RADERA</strong> för att bekräfta:
                                                </label>
                                                <input
                                                    type="text"
                                                    className="profile-input profile-delete-input"
                                                    value={deleteConfirm}
                                                    onChange={e => setDeleteConfirm(e.target.value)}
                                                    placeholder="RADERA"
                                                    autoComplete="off"
                                                />
                                                <div className="profile-delete-actions">
                                                    <button
                                                        className="profile-delete-btn"
                                                        disabled={deleteConfirm !== 'RADERA' || pending}
                                                        onClick={handleDelete}
                                                    >
                                                        <Trash2 size={14} /> Radera permanent
                                                    </button>
                                                    <button
                                                        className="btn btn-ghost"
                                                        onClick={() => { setShowDeleteZone(false); setDeleteConfirm('') }}
                                                    >
                                                        Avbryt
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </section>
                                </>
                            )}
                        </>
                    ) : isLocked ? (
                        /* ── Locked vault — user needs to authenticate ── */
                        <section className="profile-section profile-locked">
                            <div className="profile-locked-icon">🔒</div>
                            <h3>Profil låst</h3>
                            <p className="profile-hint">
                                Du har en sparad profil. Lås upp för att fortsätta.
                            </p>

                            <button
                                className="profile-unlock-btn"
                                onClick={handleUnlockPasskey}
                                disabled={pending}
                            >
                                <Key size={18} />
                                {pending ? 'Väntar på nyckelring…' : 'Lås upp med passkey'}
                            </button>

                            <div className="profile-divider">
                                <span>eller</span>
                            </div>

                            {canCreateProfile && (
                                <button className="profile-create-btn profile-create-btn--secondary" onClick={handleCreateIdentity} disabled={pending}>
                                    <Key size={16} /> Skapa ny profil
                                </button>
                            )}

                            <div className="profile-import-section">
                                <p className="profile-hint">Har du en backup-nyckel? Klistra in den här:</p>
                                <textarea
                                    className="profile-import-input"
                                    placeholder="sb-key:..."
                                    rows={3}
                                    value={importPayload}
                                    onChange={e => setImportPayload(e.target.value)}
                                />
                                <button className="profile-import-btn" onClick={handleImportIdentity} disabled={pending}>Importera</button>
                            </div>
                        </section>
                    ) : (
                        /* ── No vault — first visit onboarding ── */
                        <section className="profile-section profile-onboarding">
                            <div className="profile-onboarding-icon">🔐</div>
                            <h3>Welcome to SecureBlob</h3>
                            <p>
                                {canCreateProfile
                                    ? 'Create a profile to save your workspaces and collaborate with others.'
                                    : 'New profile signup is temporarily unavailable. Import a backup key to sign in.'}
                            </p>

                            {canCreateProfile && (
                                <button className="profile-create-btn" onClick={handleCreateIdentity} disabled={pending}>
                                    <Key size={18} /> Create new profile
                                </button>
                            )}

                            {canCreateProfile && (
                                <div className="profile-divider">
                                    <span>or</span>
                                </div>
                            )}

                            <div className="profile-import-section">
                                <p className="profile-hint">Already have a profile? Paste your backup key:</p>
                                <textarea
                                    className="profile-import-input"
                                    placeholder="sb-key:..."
                                    rows={3}
                                    value={importPayload}
                                    onChange={e => setImportPayload(e.target.value)}
                                />
                                <button className="profile-import-btn" onClick={handleImportIdentity} disabled={pending}>Sign in</button>
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </>
    )
}
